import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import {
  BehaviorSubject,
  catchError,
  filter,
  Observable,
  Subject,
  switchMap,
  take,
  takeUntil,
  throwError,
} from 'rxjs';
import { environment } from 'src/environments/environment.development';
import { AuthService } from '../modules/app/auth/auth.service';
import { iEmployeeResultDto } from './Interfaces/employee';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private cancelRequests$ = new Subject<void>();
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> =
    new BehaviorSubject<string | null>(null);

  constructor(
    private _authServ: AuthService,
    private _modalServ: NgbModal,
    private _toastrServ: ToastrService,
    private _router: Router,
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const isCustomerPortal = request.headers.get('isCustomerPortal') === 'true';
    if (isCustomerPortal) {
      request = request.clone({
        headers: request.headers.delete('isCustomerPortal'),
      });
      return next.handle(request);
    }

    const token = this._authServ._dataServ.tokenValue;
    const isApi =
      request.url.startsWith(environment.base_url) ||
      request.url.startsWith(environment.easy_form_base_url) ||
      request.url.startsWith(environment.payroll_base_url) ||
      request.url.startsWith(environment.polls_base_url) ||
      request.url.startsWith(environment.leadKonnekt_base_url) ||
      request.url.startsWith(environment.accounting_base_url) ||
      request.url.startsWith(environment.inventory_base_url) ||
      // request.url.startsWith(environment.easy_fee_base_url) ||
      request.url.startsWith(environment.recruitmentBaseUrl) ||
      request.url.startsWith(environment.loans_and_advances_base_url) ||
      request.url.startsWith(environment.invoices_base_url) ||
      request.url.startsWith(environment.appraisal_base_url) ||
      request.url.startsWith(environment.ai_url);

    if (token && isApi) {
      request = this.addTokenHeaders(request, token);
    }

    const skipStationId = request.headers.get('skipStationId') === 'true';
    if (skipStationId) {
      request = this.removeStationId(request);
      // request = request.clone({ headers: request.headers.delete('skipStationId') });
    }
    const skipCompanyId = request.headers.get('skipCompanyId') === 'true';
    if (skipCompanyId) {
      request = this.removeCompanyId(request);
      // request = request.clone({ headers: request.headers.delete('skipCompanyId') });
    }

    const skipGlobalErrorToast =
      request.headers.get('skipGlobalErrorToast') === 'true';

    const isRefreshTokenRequest = request.url.includes(
      '/api/User/refresh-token',
    );

    if (isRefreshTokenRequest) {
      return next.handle(request);
    }

    return next.handle(request).pipe(
      takeUntil(this.cancelRequests$),
      catchError((error) => {
        if (
          error instanceof HttpErrorResponse &&
          error.status === 401 &&
          isApi
        ) {
          return this.handle401Error(request, next);
        }

        if (
          error instanceof HttpErrorResponse &&
          [400, 403].includes(error.status) &&
          isApi
        ) {
          const backendMessage =
            this.extractBackendMessage(error.error) || error.message || '';
          const normalizedMessage = String(backendMessage).toLowerCase();
          const isExpiryError =
            normalizedMessage.includes('expired') ||
            normalizedMessage.includes('workspace has expired') ||
            normalizedMessage.includes('subscription has expired') ||
            normalizedMessage.includes('workspace expired') ||
            request.url.toLowerCase().includes('/api/workspace/getbyid');

          const message =
            String(backendMessage).trim() ||
            `Request failed with status ${error.status}`;
          const isActiveTrackStatusError =
            request.url.toLowerCase().includes('/api/taskstatus/add') &&
            normalizedMessage.includes('cannot change task status') &&
            normalizedMessage.includes('active time track');

          // Employee limit errors are handled by the component with a user-friendly message
          const isEmployeeLimitError =
            normalizedMessage.includes('employee limit') ||
            normalizedMessage.includes('maximum allowed');

          if (error.status === 403) {
            this._toastrServ.clear();

            this._authServ._dataServ.setWorkspaceExpiry({
              active: true,
              message,
            });
            this._modalServ.dismissAll();
          } else if (isExpiryError) {
            const message =
              String(backendMessage).trim() ||
              'Your workspace subscription has expired.';
            this._toastrServ.clear();

            this._authServ._dataServ.setWorkspaceExpiry({
              active: true,
              message,
            });
            this._modalServ.dismissAll();
          } else if (isActiveTrackStatusError) {
            // Let feature-level UI show this specific error inline instead of global toaster.
          } else if (isEmployeeLimitError || skipGlobalErrorToast) {
            // Let feature-level UI show this specific error instead of global toaster.
          } else {
            this._toastrServ.error(message);
          }
        }

        return throwError(() => error);
      }),
    );
  }

  private addTokenHeaders(
    request: HttpRequest<any>,
    token: string,
  ): HttpRequest<any> {
    let headers = request.headers.set('Authorization', `Bearer ${token}`);
    const emp = this._authServ._dataServ.employeeValue;

    if (emp) {
      const { workspaceId, companyId }: iEmployeeResultDto = emp;
      const cCompanyId = this._authServ._dataServ.companyIdValue;
      const cStationId = this._authServ._dataServ.stationIdValue;

      // if (headers.get('WorkspaceId') != environment.emptyGuid && workspaceId)
      //   headers = headers.set('WorkspaceId', workspaceId);

      if (workspaceId && !headers.has('WorkspaceId')) {
        headers = headers.set('WorkspaceId', workspaceId);
      }

      const scid = this._authServ._dataServ.hasPermission('L1008')
        ? cCompanyId || null
        : companyId;
      const ssid = cStationId
        ? cStationId
        : this._authServ._dataServ.empStationsValue.length == 1
          ? this._authServ._dataServ.empStationsValue[0].stationId
          : null;

      if (scid && !headers.get('CompanyId'))
        headers = headers.set('CompanyId', scid);
      if (ssid && !headers.get('StationId'))
        headers = headers.set('StationId', ssid);

      if (headers.get('CompanyId') === environment.emptyGuid)
        headers = headers.delete('CompanyId');
      if (headers.get('StationId') === environment.emptyGuid)
        headers = headers.delete('StationId');
      if (headers.get('WorkspaceId') === environment.emptyGuid)
        headers = headers.delete('WorkspaceId');
    } else {
      const cus = this._authServ._dataServ.customerValue;
      if (cus) {
        const { workspaceId, companyId, stationId } = cus;
        headers = headers.set('WorkspaceId', workspaceId);
        if (companyId) headers = headers.set('CompanyId', companyId);
        if (stationId) headers = headers.set('StationId', stationId);
      }
    }

    return request.clone({ headers });
  }

  private extractBackendMessage(errorBody: any): string {
    if (!errorBody) {
      return '';
    }

    if (typeof errorBody === 'string') {
      return errorBody;
    }

    if (errorBody.error) {
      return this.extractBackendMessage(errorBody.error);
    }

    if (errorBody.message && typeof errorBody.message === 'string') {
      return errorBody.message;
    }

    if (errorBody.error && typeof errorBody.error === 'string') {
      return errorBody.error;
    }

    if (Array.isArray(errorBody.errors)) {
      return errorBody.errors
        .map((item: any) =>
          typeof item === 'string' ? item : JSON.stringify(item),
        )
        .join(' ');
    }

    return Object.values(errorBody)
      .filter((value) => typeof value === 'string')
      .join(' ');
  }

  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this._authServ.refreshToken().pipe(
        catchError((err) => {
          this.isRefreshing = false;
          this.cancelAllRequests();
          this._modalServ.dismissAll();
          this._toastrServ.clear();
          this._toastrServ.error('Session Expired.');
          this._authServ.Logout();
          return throwError(() => err);
        }),
        switchMap((newToken: string) => {
          this.isRefreshing = false;
          this._authServ._dataServ.setToken(newToken);
          this.refreshTokenSubject.next(newToken);
          return next.handle(this.addTokenHeaders(request, newToken));
        }),
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((token) =>
          next.handle(this.addTokenHeaders(request, token!)),
        ),
      );
    }
  }

  private cancelAllRequests(): void {
    this.cancelRequests$.next();
  }

  private removeStationId(request: HttpRequest<any>): HttpRequest<any> {
    const headers = request.headers.delete('StationId').delete('SkipStationId');
    return request.clone({ headers });
  }
  private removeCompanyId(request: HttpRequest<any>): HttpRequest<any> {
    const headers = request.headers.delete('CompanyId').delete('SkipCompanyId');
    return request.clone({ headers });
  }
}
